import { LogEntry, LogResponse } from "../../types";
import { createLog } from "../../lib/admin";

// ─── Log configuration ───────────────────────────────────────────────────────

export const STORE_LOG_ENTITY_TYPES = [
    'auth',
    'store',
    'sync',
    'storeSettings',
    'customization',
    'components',
];

export const GLOBAL_LOG_ENTITY_TYPES = [
    'auth',
    'user',
];

const LOG_LABELS: Record<string, string> = {
    auth: 'Login',
    user: 'Users & permissions',
    store: 'Store (create / cancel)',
    sync: 'Sync',
    storeSettings: 'Store settings',
    customization: 'Customization',
    components: 'Components',
    syncSettings: 'Sync Settings',
    syncType: 'Sync Type',
    manualSync: 'Manual Sync',
    retailSystem: 'RetailSystem',
    shopify: 'Shopify',
    csv: 'CSV',
    settings: 'Settings',
    tagline: 'Tagline',
    whyChooseUs: 'Values - Why Choose Us',
    bannerLandscape: 'Landscape Banner',
    bannerSplit: 'Split Banner',
    carousel: 'Carousel',
    sideBySide: 'Side By Side',
    mirrorDatabase: 'Mirror Database',
    searchIndex: 'Search Index',
};

export const LOG_SEARCH_SCOPES = [
    { value: '', label: 'All' },
    { value: 'actor', label: 'Actor' },
    { value: 'target', label: 'Affected user' },
];

export const LOG_SECTIONS: Record<string, string[]> = {
    sync: ['syncSettings', 'syncType', 'manualSync'],
    storeSettings: ['retailSystem', 'shopify', 'csv', 'settings'],
    customization: [
        'tagline',
        'whyChooseUs',
        'bannerLandscape',
        'bannerSplit',
        'carousel',
        'sideBySide',
    ],
    components: ['mirrorDatabase', 'searchIndex'],
};

export const LOGS_PAGE_SIZE = 50;

// ─── Log helpers ──────────────────────────────────────────────────────────────

export const getLogLabel = (value?: string): string => {
    if (!value) return '—';
    return LOG_LABELS[value] || value;
};

export const getLogAction = (log: LogResponse): string => log.action || '—';

export const getLogTarget = (log: LogResponse): string => log.newValue?.email || '';

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const toRangeBoundary = (
    dateTime: string,
    boundary: 'start' | 'end',
    isUtc: boolean,
): string | undefined => {
    if (!dateTime) return undefined;

    const seconds = boundary === 'start' ? '00.000' : '59.999';
    const date = new Date(`${dateTime}:${seconds}${isUtc ? 'Z' : ''}`);

    return isNaN(date.getTime()) ? undefined : date.toISOString();
};

const pad = (n: number) => String(n).padStart(2, '0');

export const formatLogDate = (date: string, isUtc: boolean): string => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';

    if (isUtc) {
        return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
    }

    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ─── Logged actions ───────────────────────────────────────────────────────────

// Destructive actions are logged before they run, and a failed log stops them
const LOG_FAILED_MESSAGE = "Could not record this action in the activity log, so it was not performed. Please try again.";

export const runLoggedAction = async (
    entry: LogEntry,
    run: () => Promise<string>,
): Promise<string> => {
    try {
        await createLog(entry, true);
    } catch {
        return LOG_FAILED_MESSAGE;
    }

    return run();
};